import { Component, computed, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Uc2NetworkBladeComponent } from '../uc2-network-blade/uc2-network-blade.component';
import { formatTimestamp, getConfidenceRating, StepStatus, WAT_TIME_ZONE, WorkflowBadgeStatus, WorkflowItem } from '#models';
import type { ConfidenceRating, ReportBlock } from '#models';
import { TelemetryService } from '#services';
import { TelemetryEvent } from '#enums';
import { isProductionDeviationItem } from '../../mappers/production-deviation-workflow.mapper';
import { WAT_DATE_FORMAT } from '../../mappers/persisted-case.mapper';

@Component({
  selector: 'app-detail-header',
  imports: [DatePipe, Uc2NetworkBladeComponent],
  templateUrl: './detail-header.component.html',
  styleUrl: './detail-header.component.scss',
})
export class DetailHeaderComponent {
  item = input.required<WorkflowItem>();
  summaryBlocks = input<ReportBlock[] | null>(null);
  summaryText = input<string>('');
  previewAssetCodes = input<string[]>([]);
  private _telemetry = inject(TelemetryService);
  private _networkCreatedDate = signal<string | null>(null);
  private _networkBladeOpen = signal(false);
  networkBladeOpen = this._networkBladeOpen.asReadonly();
  readonly dateFormat = WAT_DATE_FORMAT;
  isProductionDeviation = computed(() => isProductionDeviationItem(this.item()));
  productionDay = computed(() => this._validTimestamp(this.item().dataForDate));
  detectedUtc = computed(() => this._validTimestamp(this.item().createdDate));
  caseId = computed(() => {
    const item = this.item();
    return item.persistedCase?.agentCaseId?.toString() ?? item.id.replace(/^case-([1-9]\d*)$/, '$1');
  });

  private _networkContextTrigger = viewChild<ElementRef<HTMLButtonElement>>('networkContextTrigger');

  private _validTimestamp(value: string | null | undefined): string | null {
    return value && Number.isFinite(Date.parse(value)) ? value : null;
  }

  onNetworkCreatedDateLoaded(value: string | null): void {
    this._networkCreatedDate.set(value);
  }

  primaryAssetLabel(): string | null {
    return this.previewAssetCodes()[0] ?? this.item().identity?.stream ?? null;
  }

  onNetworkContextOpened(): void {
    this._networkBladeOpen.set(true);
    this._telemetry.trackAppEvent(TelemetryEvent.CaseNetworkViewed, { workflowId: this.item().id });
  }

  onNetworkContextClosed(): void {
    this._networkBladeOpen.set(false);
    queueMicrotask(() => this._networkContextTrigger()?.nativeElement.focus());
  }

  confBucket(level: number): ConfidenceRating {
    return getConfidenceRating(level);
  }

  getConfidenceLabel(): string {
    const confidence = this.item().identity?.confidenceLevel ?? 0;
    if (confidence <= 0) {
      return 'Unknown';
    }
    return `${getConfidenceRating(confidence)} · ${Math.round(confidence)}%`;
  }

  getBadgeClass(badge: WorkflowBadgeStatus): string {
    const map: Record<WorkflowBadgeStatus, string> = {
      [WorkflowBadgeStatus.New]: 'badge-new',
      [WorkflowBadgeStatus.AwaitingDecision]: 'badge-waiting',
      [WorkflowBadgeStatus.Closed]: 'badge-closed',
      [WorkflowBadgeStatus.Proposed]: 'badge-new',
    };
    return map[badge];
  }

  getBadgeLabel(badge: WorkflowBadgeStatus): string {
    const map: Record<WorkflowBadgeStatus, string> = {
      [WorkflowBadgeStatus.New]: 'New',
      [WorkflowBadgeStatus.AwaitingDecision]: 'Awaiting Decision',
      [WorkflowBadgeStatus.Closed]: 'Closed',
      [WorkflowBadgeStatus.Proposed]: 'Agent finding',
    };
    return map[badge];
  }

  getActionTasks(actionText: string | undefined): string[] {
    if (!actionText) {
      return [];
    }

    return actionText
      .split(';')
      .map(task => task.trim())
      .filter(task => task.length > 0);
  }

  getPreviewTimestamp(): string {
    const networkCreatedDate = this._networkCreatedDate();
    if (networkCreatedDate) {
      const parsed = Date.parse(networkCreatedDate);
      if (!Number.isNaN(parsed)) {
        return formatTimestamp(parsed, { seconds: true, timeZone: WAT_TIME_ZONE });
      }
    }

    const timestamps = (this.item().steps ?? [])
      .map(step => step.completedOn)
      .filter((value): value is string => !!value)
      .map(value => Date.parse(value))
      .filter(value => !Number.isNaN(value));

    if (timestamps.length === 0) {
      return '--';
    }

    return formatTimestamp(Math.max(...timestamps), { seconds: true, timeZone: WAT_TIME_ZONE });
  }

  getMostRecentStepTitle(): string {
    const steps = this.item().steps ?? [];
    if (steps.length === 0) {
      return '--';
    }

    const firstPending = steps.find(step => step.status === StepStatus.Pending);
    if (firstPending?.title?.trim()) {
      return firstPending.title.trim();
    }

    const latestCompleted = steps
      .filter(step => !!step.completedOn)
      .sort((left, right) => {
        const leftDate = Date.parse(left.completedOn ?? '');
        const rightDate = Date.parse(right.completedOn ?? '');
        const safeLeft = Number.isNaN(leftDate) ? Number.MIN_SAFE_INTEGER : leftDate;
        const safeRight = Number.isNaN(rightDate) ? Number.MIN_SAFE_INTEGER : rightDate;
        return safeRight - safeLeft;
      })[0];

    if (latestCompleted?.title?.trim()) {
      return latestCompleted.title.trim();
    }

    const latestStep = [...steps].sort((left, right) => right.number - left.number)[0];
    return latestStep?.title?.trim() || '--';
  }

  getAssetInlineText(): string {
    const raw = this.item().asset?.trim();
    if (!raw) {
      return '';
    }

    const assets = raw
      .split(/[;,]/)
      .map(value => value.trim())
      .filter(value => value.length > 0);

    return assets.join(' · ');
  }
}
